import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft,
  Building2,
  Upload,
  FileText,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import BankAnalysisVisuals from './BankAnalysisVisuals';

const API = API_BASE;

const BankAnalysisApp = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client');
  
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [clients, setClients] = useState([]);
  
  const [selectedClient, setSelectedClient] = useState(clientId || undefined);
  const [notes, setNotes] = useState('');
  
  // Manual entry mode
  const [manualMode, setManualMode] = useState(false);
  const [manualData, setManualData] = useState({
    avg_monthly_income: '',
    avg_ending_balance: '',
    min_monthly_deposit: '',
    min_ending_balance: '',
    total_months_analyzed: '3'
  });

  useEffect(() => {
    fetchClients();
    if (id && id !== 'new') {
      fetchAnalysis();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/bank-analyses/${id}`);
      setAnalysis(response.data);
      setSelectedClient(response.data.client_id || undefined);
      setNotes(response.data.notes || '');
    } catch (error) {
      toast.error('Error al cargar el análisis');
      navigate('/apps/bank-analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/bank-analyses`, {
        client_id: selectedClient && selectedClient !== 'none' ? selectedClient : null,
        notes
      });
      toast.success('Análisis bancario creado');
      navigate(`/apps/bank-analysis/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear análisis');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (files) => {
    if (!analysis || files.length === 0) return;
    
    setUploading(true);
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }

    try {
      // Always use local parsing (free and fast)
      const url = `${API}/bank-analyses/${analysis.id}/upload?use_ai=false`;
      const response = await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${response.data.files_analyzed.length} archivo(s) analizados exitosamente`);
      fetchAnalysis();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al analizar archivos');
    } finally {
      setUploading(false);
    }
  };

  const handleManualSave = async () => {
    if (!analysis) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/bank-analyses/${analysis.id}/manual`, {
        avg_monthly_income: parseFloat(manualData.avg_monthly_income) || 0,
        avg_ending_balance: parseFloat(manualData.avg_ending_balance) || 0,
        min_monthly_deposit: parseFloat(manualData.min_monthly_deposit) || 0,
        min_ending_balance: parseFloat(manualData.min_ending_balance) || 0,
        total_months_analyzed: parseInt(manualData.total_months_analyzed) || 3,
        notes
      });
      toast.success('Datos guardados');
      fetchAnalysis();
      setManualMode(false);
    } catch (error) {
      toast.error('Error al guardar datos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  // New analysis form
  if (!id || id === 'new') {
    return (
      <div className="space-y-6" data-testid="new-bank-analysis-page">
        <Button variant="ghost" onClick={() => navigate('/apps/bank-analysis')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a Análisis Bancarios
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
              Nuevo Análisis Bancario
            </CardTitle>
            <CardDescription>Analiza estados de cuenta bancarios</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Cliente (Opcional)</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar cliente..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin cliente</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notas sobre el análisis..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => navigate('/apps/bank-analysis')}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={saving}>
                {saving ? 'Creando...' : 'Crear Análisis'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Existing analysis view
  return (
    <div className="space-y-6" data-testid="bank-analysis-detail-page">
      <Button variant="ghost" onClick={() => navigate('/apps/bank-analysis')}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver a Análisis Bancarios
      </Button>

      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-600" />
                Análisis Bancario
              </CardTitle>
              <CardDescription>
                {analysis?.client_name ? `Cliente: ${analysis.client_name}` : 'Sin cliente asociado'}
              </CardDescription>
            </div>
            <Badge variant={analysis?.status === 'completed' ? 'default' : 'secondary'}>
              {analysis?.status === 'completed' ? 'Completado' : 'Borrador'}
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle>Subir Estados de Cuenta</CardTitle>
          <CardDescription>Sube archivos PDF para análisis automático</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="font-medium mb-2">Arrastra archivos aquí o haz clic para seleccionar</p>
            <p className="text-sm text-muted-foreground mb-4">Puedes subir múltiples archivos PDF</p>
            
            <input
              type="file"
              accept=".pdf,image/*"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files);
                if (files.length > 0) {
                  // Automatically start analysis
                  handleUpload(files);
                  e.target.value = ''; // Reset input
                }
              }}
              className="hidden"
              id="bank-upload"
            />
            
            <Button 
              variant="outline" 
              onClick={() => document.getElementById('bank-upload').click()}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Analizando...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Seleccionar Archivos
                </>
              )}
            </Button>
          </div>

          {analysis?.files_analyzed?.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="font-medium text-sm">Archivos Analizados:</h4>
              {analysis.files_analyzed.map((file, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 rounded bg-muted text-sm">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span>{file}</span>
                  <CheckCircle className="w-4 h-4 text-emerald-600 ml-auto" />
                </div>
              ))}
            </div>
          )}

          <Separator className="my-4" />

          <Button variant="outline" onClick={() => setManualMode(!manualMode)} className="w-full">
            {manualMode ? 'Cancelar Entrada Manual' : 'Ingresar Datos Manualmente'}
          </Button>
        </CardContent>
      </Card>

      {/* Manual Entry */}
      {manualMode && (
        <Card>
          <CardHeader>
            <CardTitle>Entrada Manual de Datos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ingreso Mensual Promedio ($)</Label>
                <Input
                  type="number"
                  value={manualData.avg_monthly_income}
                  onChange={(e) => setManualData({...manualData, avg_monthly_income: e.target.value})}
                  placeholder="15000"
                />
              </div>
              <div className="space-y-2">
                <Label>Balance Final Promedio ($)</Label>
                <Input
                  type="number"
                  value={manualData.avg_ending_balance}
                  onChange={(e) => setManualData({...manualData, avg_ending_balance: e.target.value})}
                  placeholder="8500"
                />
              </div>
              <div className="space-y-2">
                <Label>Depósito Mínimo ($)</Label>
                <Input
                  type="number"
                  value={manualData.min_monthly_deposit}
                  onChange={(e) => setManualData({...manualData, min_monthly_deposit: e.target.value})}
                  placeholder="12000"
                />
              </div>
              <div className="space-y-2">
                <Label>Balance Final Mínimo ($)</Label>
                <Input
                  type="number"
                  value={manualData.min_ending_balance}
                  onChange={(e) => setManualData({...manualData, min_ending_balance: e.target.value})}
                  placeholder="6000"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Meses Analizados</Label>
              <Input
                type="number"
                value={manualData.total_months_analyzed}
                onChange={(e) => setManualData({...manualData, total_months_analyzed: e.target.value})}
                min="1"
                max="12"
              />
            </div>
            <Button onClick={handleManualSave} disabled={saving} className="w-full">
              {saving ? 'Guardando...' : 'Guardar Datos Manuales'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {analysis?.status === 'completed' && (
        <>
          {/* Visual Dashboard */}
          {analysis?.months?.length > 0 && (
            <BankAnalysisVisuals analysis={analysis} />
          )}

          {/* Legacy metrics (shown when no months data, e.g. manual entry) */}
          {(!analysis?.months || analysis.months.length === 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  Resultados del Análisis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                    <p className="text-sm text-muted-foreground">Ingreso Real Mensual</p>
                    <p className="text-xl font-semibold text-emerald-700 dark:text-emerald-400">
                      ${analysis.avg_monthly_income?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                    <p className="text-sm text-muted-foreground">Balance Final Prom.</p>
                    <p className="text-xl font-semibold text-blue-700 dark:text-blue-400">
                      ${analysis.avg_ending_balance?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                    <p className="text-sm text-muted-foreground">Depósito Mínimo</p>
                    <p className="text-xl font-semibold text-amber-700 dark:text-amber-400">
                      ${analysis.min_monthly_deposit?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                    <p className="text-sm text-muted-foreground">Balance Mín. Final</p>
                    <p className="text-xl font-semibold text-purple-700 dark:text-purple-400">
                      ${analysis.min_ending_balance?.toLocaleString() || '0'}
                    </p>
                  </div>
                </div>

                {analysis.flags?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {analysis.flags.map((flag, idx) => (
                      <div key={idx} className={cn(
                        "flex items-center gap-2 p-3 rounded-lg",
                        flag.includes('MCA') || flag.includes('ALTO RIESGO') || flag.includes('DIARIOS')
                          ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400"
                          : "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400"
                      )}>
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm">{flag}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Validation Alerts Section */}
      {analysis?.alerts?.length > 0 && (
        <Card className="border-red-200 dark:border-red-800">
          <CardHeader className="bg-red-50 dark:bg-red-900/20">
            <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Alertas de Validación de Documentos
            </CardTitle>
            <CardDescription className="text-red-600 dark:text-red-300">
              Se detectaron {analysis.alerts.length} alerta(s) que requieren revisión manual
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 pt-4">
            {analysis.alerts.map((alert, idx) => (
              <div 
                key={idx} 
                className={cn(
                  "p-4 rounded-lg border-l-4",
                  alert.severity === 'critical' 
                    ? "bg-red-50 dark:bg-red-900/30 border-red-500 text-red-800 dark:text-red-200" 
                    : "bg-amber-50 dark:bg-amber-900/30 border-amber-500 text-amber-800 dark:text-amber-200"
                )}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className={cn(
                    "w-5 h-5 flex-shrink-0 mt-0.5",
                    alert.severity === 'critical' ? "text-red-600" : "text-amber-600"
                  )} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={alert.severity === 'critical' ? 'destructive' : 'outline'} className="text-xs">
                        {alert.severity === 'critical' ? 'CRÍTICO' : 'ADVERTENCIA'}
                      </Badge>
                      <span className="text-xs font-mono bg-white/50 dark:bg-black/20 px-1.5 py-0.5 rounded">
                        {alert.type}
                      </span>
                    </div>
                    <p className="font-medium">{alert.message}</p>
                    {alert.details && (
                      <div className="mt-2 text-sm opacity-80">
                        {Array.isArray(alert.details) ? (
                          <ul className="space-y-1 list-disc list-inside">
                            {alert.details.map((detail, i) => (
                              <li key={i}>{typeof detail === 'object' ? JSON.stringify(detail) : detail}</li>
                            ))}
                          </ul>
                        ) : typeof alert.details === 'object' ? (
                          <ul className="space-y-1 list-disc list-inside">
                            {Object.entries(alert.details).map(([key, value], i) => (
                              <li key={i}>
                                <span className="font-medium">{key.replace(/_/g, ' ')}:</span>{' '}
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p>{String(alert.details)}</p>
                        )}
                      </div>
                    )}
                    {alert.confidence && (
                      <p className="mt-2 text-xs opacity-70">
                        Confianza del análisis: {alert.confidence}%
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Account Holders Detected */}
      {analysis?.account_holders_detected?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Titulares de Cuenta Detectados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {analysis.account_holders_detected.map((name, idx) => (
                <Badge key={idx} variant="outline">{name}</Badge>
              ))}
            </div>
            {analysis.account_holders_detected.length > 1 && (
              <p className="text-xs text-amber-600 mt-2">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Se detectaron múltiples nombres diferentes en los estados de cuenta
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Document Validation Summary */}
      {analysis?.document_validation_summary && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Resumen de Validación de Documentos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold">{analysis.document_validation_summary.total_documents || 0}</p>
                <p className="text-xs text-muted-foreground">Documentos Analizados</p>
              </div>
              <div>
                <p className={cn(
                  "text-2xl font-bold",
                  analysis.document_validation_summary.documents_with_issues > 0 ? "text-red-600" : "text-emerald-600"
                )}>
                  {analysis.document_validation_summary.documents_with_issues || 0}
                </p>
                <p className="text-xs text-muted-foreground">Con Problemas</p>
              </div>
              <div>
                <p className={cn(
                  "text-2xl font-bold",
                  analysis.document_validation_summary.average_confidence >= 70 ? "text-emerald-600" : 
                  analysis.document_validation_summary.average_confidence >= 50 ? "text-amber-600" : "text-red-600"
                )}>
                  {analysis.document_validation_summary.average_confidence || 0}%
                </p>
                <p className="text-xs text-muted-foreground">Confianza Prom.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BankAnalysisApp;
